import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { HelpPanel } from '../components/ui/help-panel';
import { reconcileBuckHelp } from '../lib/helpContent';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  Sparkles,
  Settings2,
  ChevronDown,
  FileText,
  Building2,
  HelpCircle,
  MoreHorizontal,
  Check,
  X,
  CircleDollarSign,
  TrendingUp,
  TrendingDown,
  Upload,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const MODES = [
  { value: 'off', label: 'Off', description: 'No automatic suggestions' },
  { value: 'suggest_only', label: 'Suggest Only', description: 'Show suggestions for review' },
  { value: 'auto_apply', label: 'Auto Apply', description: 'Automatically approve high confidence matches' },
];

export default function ReconcileBuck() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('high_confidence');
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Settings
  const [settings, setSettings] = useState({
    mode: 'suggest_only',
    auto_apply_threshold: 0.90,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Bank accounts and statements
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('all');
  const [statements, setStatements] = useState([]);
  const [selectedStatementId, setSelectedStatementId] = useState('all');
  
  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [counts, setCounts] = useState({});
  
  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/reconcile-buck/settings?workspace_id=${currentWorkspace.workspace_id}`
      );
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, [currentWorkspace]);

  // Fetch bank accounts
  const fetchBankAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/accounts?workspace_id=${currentWorkspace.workspace_id}`
      );
      if (response.ok) {
        const data = await response.json();
        setBankAccounts(data.accounts || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    }
  }, [currentWorkspace]);

  // Fetch statements for selected account
  const fetchStatements = useCallback(async () => {
    if (!currentWorkspace || selectedAccountId === 'all') {
      setStatements([]);
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/list/${selectedAccountId}`
      );
      if (response.ok) {
        const data = await response.json();
        setStatements(data.statements || []);
      }
    } catch (error) {
      console.error('Failed to fetch statements:', error);
    }
  }, [currentWorkspace, selectedAccountId]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!currentWorkspace) return;
    
    // If no statement selected, we need to aggregate from all statements
    // For now, show empty state prompting user to select a statement
    if (selectedStatementId === 'all' && selectedAccountId !== 'all') {
      // Get all pending statements and aggregate
      let allSuggestions = [];
      let allCounts = { all: 0, high_confidence: 0, needs_review: 0, unmatched: 0, pending: 0, approved: 0 };
      
      for (const stmt of statements) {
        try {
          const response = await fetch(
            `${API_URL}/api/banking/statements/${stmt.statement_id}/suggestions?tab=${activeTab}`
          );
          if (response.ok) {
            const data = await response.json();
            allSuggestions = allSuggestions.concat(data.suggestions || []);
            Object.keys(data.counts || {}).forEach(k => {
              allCounts[k] = (allCounts[k] || 0) + (data.counts[k] || 0);
            });
          }
        } catch (error) {
          console.error('Failed to fetch suggestions for statement:', stmt.statement_id);
        }
      }
      
      setSuggestions(allSuggestions);
      setCounts(allCounts);
      return;
    }
    
    if (selectedStatementId === 'all') {
      setSuggestions([]);
      setCounts({});
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${selectedStatementId}/suggestions?tab=${activeTab}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setCounts(data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, [currentWorkspace, selectedStatementId, selectedAccountId, statements, activeTab]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchBankAccounts()]);
      setLoading(false);
    };
    load();
  }, [fetchSettings, fetchBankAccounts]);

  // Fetch statements when account changes
  useEffect(() => {
    fetchStatements();
    setSelectedStatementId('all');
  }, [fetchStatements, selectedAccountId]);

  // Fetch suggestions when statement or tab changes
  useEffect(() => {
    fetchSuggestions();
    setSelectedIds(new Set());
  }, [fetchSuggestions]);

  // Save settings
  const handleSaveSettings = async () => {
    if (!currentWorkspace) return;
    
    setSavingSettings(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reconcile-buck/settings?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        }
      );
      
      if (response.ok) {
        toast.success('Settings saved');
        setShowSettings(false);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Approve suggestion
  const handleApprove = async (suggestionId, action = null) => {
    try {
      const url = `${API_URL}/api/banking/statements/suggestions/${suggestionId}/approve${action ? `?action=${action}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        });
      
      if (response.ok) {
        toast.success('Approved');
        fetchSuggestions();
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  // Reject suggestion
  const handleReject = async (suggestionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/suggestions/${suggestionId}/reject`,
        {
          method: 'POST',
          }
      );
      
      if (response.ok) {
        toast.success('Rejected');
        fetchSuggestions();
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  // Bulk approve
  const handleBulkApprove = async (action = null) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.info('No suggestions selected');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/suggestions/bulk-approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion_ids: ids, action }),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved_count} suggestions`);
        fetchSuggestions();
        setSelectedIds(new Set());
      }
    } catch (error) {
      toast.error('Failed to bulk approve');
    }
  };

  // Bulk approve all high confidence
  const handleApproveAllHighConfidence = async () => {
    const highConfIds = suggestions
      .filter(s => s.confidence_score >= 0.90 && s.status === 'pending')
      .map(s => s.suggestion_id);
    
    if (highConfIds.length === 0) {
      toast.info('No high confidence suggestions to approve');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/suggestions/bulk-approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion_ids: highConfIds }),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved_count} high confidence matches`);
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to bulk approve');
    }
  };

  // Toggle selection
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all visible
  const toggleSelectAll = () => {
    const pendingIds = suggestions.filter(s => s.status === 'pending').map(s => s.suggestion_id);
    if (selectedIds.size === pendingIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="reconcile-buck-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Smart Reconcile
          </h1>
          <p className="text-sm text-muted-foreground">
            AI Buck analyzes your statements and suggests matches with confidence scores
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/banking/statement-upload')}>
            <Upload className="w-4 h-4 mr-2" />
            Upload Statement
          </Button>
          
          <HelpPanel {...reconcileBuckHelp} />
          
          <Sheet open={showSettings} onOpenChange={setShowSettings}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon">
                <Settings2 className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Smart Reconcile Settings</SheetTitle>
                <SheetDescription>
                  Configure how automatic matching behaves
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="space-y-3">
                  <Label>Mode</Label>
                  {MODES.map((mode) => (
                    <div
                      key={mode.value}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        settings.mode === mode.value
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                      onClick={() => setSettings({ ...settings, mode: mode.value })}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded-full border-2 mt-0.5",
                        settings.mode === mode.value
                          ? "border-primary bg-primary"
                          : "border-muted-foreground"
                      )}>
                        {settings.mode === mode.value && (
                          <Check className="w-3 h-3 text-primary-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{mode.label}</p>
                        <p className="text-sm text-muted-foreground">{mode.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {settings.mode === 'auto_apply' && (
                  <div className="space-y-3">
                    <Label>Auto-Apply Threshold</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.auto_apply_threshold * 100]}
                        onValueChange={([v]) => setSettings({ ...settings, auto_apply_threshold: v / 100 })}
                        min={50}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-sm font-mono">
                        {Math.round(settings.auto_apply_threshold * 100)}%
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only matches with confidence above this threshold will be auto-approved
                    </p>
                  </div>
                )}
                
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full">
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Bank Account</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.bank_id} value={account.bank_id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>{account.name || account.official_name}</span>
                        {account.mask && <span className="text-muted-foreground">****{account.mask}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Statement</Label>
              <Select
                value={selectedStatementId}
                onValueChange={setSelectedStatementId}
                disabled={selectedAccountId === 'all'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Statement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statements</SelectItem>
                  {statements.map((stmt) => (
                    <SelectItem key={stmt.statement_id} value={stmt.statement_id}>
                      {stmt.start_date} to {stmt.end_date} ({stmt.line_count} lines)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button variant="outline" size="icon" onClick={fetchSuggestions}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mode Indicator */}
      <div className="flex items-center gap-2 mb-4">
        <Badge variant="outline" className={cn(
          settings.mode === 'off' ? "bg-gray-100" :
          settings.mode === 'suggest_only' ? "bg-amber-100 text-amber-700" :
          "bg-emerald-100 text-emerald-700"
        )}>
          Mode: {MODES.find(m => m.value === settings.mode)?.label}
        </Badge>
        {settings.mode === 'auto_apply' && (
          <Badge variant="outline">
            Threshold: {Math.round(settings.auto_apply_threshold * 100)}%
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="high_confidence" className="gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                High Conf.
                {counts.high_confidence > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {counts.high_confidence}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="needs_review" className="gap-1.5">
                <Clock className="w-4 h-4" />
                Review
                {counts.needs_review > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {counts.needs_review}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="unmatched" className="gap-1.5">
                <AlertCircle className="w-4 h-4" />
                Unmatched
                {counts.unmatched > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {counts.unmatched}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="all" className="gap-1.5">
                <FileText className="w-4 h-4" />
                All
                {counts.all > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {counts.all}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved" className="gap-1.5">
                <Check className="w-4 h-4" />
                Approved
                {counts.approved > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {counts.approved}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          
          <CardContent className="pt-4">
            {/* Bulk Actions */}
            {pendingCount > 0 && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedIds.size === pendingCount && pendingCount > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-sm text-muted-foreground">
                    {selectedIds.size > 0 
                      ? `${selectedIds.size} selected`
                      : `${pendingCount} pending`}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  {selectedIds.size > 0 && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleBulkApprove('cleared')}>
                        <Check className="w-4 h-4 mr-1" />
                        Mark Cleared
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkApprove('outstanding')}>
                        <Clock className="w-4 h-4 mr-1" />
                        Mark Outstanding
                      </Button>
                    </>
                  )}
                  {activeTab === 'high_confidence' && (
                    <Button size="sm" onClick={handleApproveAllHighConfidence}>
                      <Sparkles className="w-4 h-4 mr-1" />
                      Approve All High Confidence
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Suggestions Table */}
            {suggestions.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">AI Buck found no suggestions</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {selectedAccountId === 'all' 
                    ? 'Select a bank account for AI Buck to analyze'
                    : 'Upload a statement for AI Buck to start matching'}
                </p>
                <Button onClick={() => navigate('/banking/statement-upload')}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Statement
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead className="w-[80px]">Score</TableHead>
                      <TableHead>Statement Line</TableHead>
                      <TableHead>Matched To</TableHead>
                      <TableHead>Suggested</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suggestions.map((sugg) => (
                      <TableRow key={sugg.suggestion_id}>
                        <TableCell>
                          {sugg.status === 'pending' && (
                            <Checkbox
                              checked={selectedIds.has(sugg.suggestion_id)}
                              onCheckedChange={() => toggleSelection(sugg.suggestion_id)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              "font-mono",
                              sugg.confidence_score >= 0.90
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : sugg.confidence_score >= 0.60
                                ? "bg-amber-100 text-amber-700 border-amber-200"
                                : "bg-red-100 text-red-700 border-red-200"
                            )}
                          >
                            {Math.round(sugg.confidence_score * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <p className="font-medium truncate max-w-[200px]">
                              {sugg.statement_description}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{sugg.statement_date}</span>
                              <span className={cn(
                                "font-mono",
                                sugg.statement_amount < 0 ? "text-red-600" : "text-emerald-600"
                              )}>
                                {formatCurrency(sugg.statement_amount)}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sugg.matched_id ? (
                            <div className="space-y-0.5">
                              <p className="truncate max-w-[200px]">
                                {sugg.matched_description}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{sugg.matched_date}</span>
                                <span className="font-mono">
                                  {formatCurrency(sugg.matched_amount)}
                                </span>
                              </div>
                              {sugg.reason_codes?.length > 0 && (
                                <div className="flex gap-1 flex-wrap">
                                  {sugg.reason_codes.slice(0, 2).map((code, i) => (
                                    <Badge key={i} variant="secondary" className="text-[10px] px-1">
                                      {code.replace('_', ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic text-sm">
                              No match found
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {sugg.suggested_action?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {sugg.status === 'approved' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                              Approved
                            </Badge>
                          ) : sugg.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                              Rejected
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {sugg.status === 'pending' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleApprove(sugg.suggestion_id, 'cleared')}>
                                  <Check className="w-4 h-4 mr-2" />
                                  Approve as Cleared
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleApprove(sugg.suggestion_id, 'outstanding')}>
                                  <Clock className="w-4 h-4 mr-2" />
                                  Approve as Outstanding
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleReject(sugg.suggestion_id)}>
                                  <X className="w-4 h-4 mr-2" />
                                  Reject
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Help Section */}
      <Card className="mt-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            How AI Buck Matches Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-medium mb-2">AI Buck Confidence Levels</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  High (≥85%) — AI Buck is confident
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Medium (70-84%) — AI Buck suggests reviewing
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Low (&lt;70%) — AI Buck needs your input
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">What AI Buck Analyzes</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Amount exact match (40%)</li>
                <li>• Date within 3 days (30%)</li>
                <li>• Description similarity (20%)</li>
                <li>• Reference number match (10%)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">AI Buck Recommendations</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Review AI Buck suggestions before reconciling</li>
                <li>• High confidence matches are usually safe to approve</li>
                <li>• Investigate unmatched items for missing transactions</li>
                <li>• Use adjustments for bank fees & interest</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
